import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useNoIndex } from "../../hooks/usePageSeo";
import {
  getSignatureFormPublic,
  verifyPatientIdentity,
  submitSignedForm,
} from "../../firebase";
import {
  FileText,
  CheckCircle,
  XCircle,
  Warning,
  IdentificationCard,
  Phone,
  PencilLine,
  Keyboard,
  Eraser,
  Check,
} from "@phosphor-icons/react";
import ContentLoader from "../../components/shared/ContentLoader";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import PublicClinicHeader from "../../components/public/PublicClinicHeader";
import ModalCloseButton from "../../components/ui/ModalCloseButton";
import "../../styles/formDocument.css";
import "./SignFormPage.css";

// Wide tables must scroll inside their own container on mobile
const wrapTablesForMobile = (html) =>
  String(html || "").replace(
    /<table[\s\S]*?<\/table>/g,
    (table) => `<div class="ck-table-wrap">${table}</div>`
  );

// Stages of the signing flow
const STAGES = {
  LOADING: "loading",
  VERIFY: "verify",
  FORM: "form",
  SUBMITTING: "submitting",
  SUCCESS: "success",
  ERROR: "error",
};

// Render a fill button HTML for an unsigned slot
const renderUnsignedSlot = (slotId) =>
  `<div class="signature-slot signature-slot--empty" data-signature-slot="${slotId}">
    <div class="signature-slot-line"></div>
    <span class="signature-slot-label">חתימה ${slotId}</span>
    <button type="button" class="signature-slot-fill-btn" data-signature-fill="${slotId}">
      לחץ/י כדי לחתום
    </button>
  </div>`;

// Render a filled signature inline
const renderFilledSlot = (slotId, signature, { showActions = true } = {}) => {
  const body =
    signature.type === "drawn"
      ? `<img src="${signature.data}" class="signature-slot-img" alt="חתימה ${slotId}" />`
      : `<span class="signature-slot-typed">"${escapeHtml(signature.data)}"</span>`;

  const actions = showActions
    ? `<div class="signature-slot-actions">
        <button type="button" class="signature-slot-action" data-signature-edit="${slotId}">ערוך</button>
        <button type="button" class="signature-slot-action signature-slot-action--clear" data-signature-clear="${slotId}">נקה</button>
      </div>`
    : "";

  return `<div class="signature-slot signature-slot--filled" data-signature-slot="${slotId}">
    ${body}
    <span class="signature-slot-label">חתימה ${slotId}</span>
    ${actions}
  </div>`;
};

const escapeHtml = (str = "") =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Replace each signature placeholder with either the fill button or the captured signature
const renderSignaturePlaceholders = (html, signatures, options = {}) => {
  if (!html) return "";
  // Consume the optional nested `<div class="signature-line">` explicitly —
  // legacy editor markup nests it, and a plain non-greedy match would stop at
  // that inner </div> and leave an orphan label behind.
  return html.replace(
    /<div[^>]*class="signature-placeholder"[^>]*>(?:\s*<div[^>]*class="signature-line"[^>]*>\s*<\/div>)?[\s\S]*?<\/div>/g,
    (match) => {
      const slotMatch = match.match(/data-signature-id="([^"]+)"/);
      // Legacy placeholders without data-signature-id are treated as slot 1
      const slotId = slotMatch ? slotMatch[1] : "1";
      const signature = signatures[slotId];
      if (signature && signature.data) {
        return renderFilledSlot(slotId, signature, options);
      }
      return renderUnsignedSlot(slotId);
    }
  );
};

// Collect distinct signature slot IDs from raw form HTML
const collectSlotIds = (html) => {
  if (!html) return [];
  const ids = new Set();
  const re = /<div[^>]*class="signature-placeholder"[^>]*>/g;
  let match;
  while ((match = re.exec(html)) !== null) {
    const idMatch = match[0].match(/data-signature-id="([^"]+)"/);
    ids.add(idMatch ? idMatch[1] : "1");
  }
  return Array.from(ids);
};

function SignFormPage() {
  const { linkId } = useParams();
  useNoIndex();
  const [stage, setStage] = useState(STAGES.LOADING);
  const [linkData, setLinkData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Verification state
  const [idNumber, setIdNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  // Per-slot signatures: { "1": { data, type }, "2": { data, type } }
  const [signatures, setSignatures] = useState({});
  const [submitError, setSubmitError] = useState("");

  // Signature modal state
  const [activeSlotId, setActiveSlotId] = useState(null);
  const [signatureType, setSignatureType] = useState("drawn");
  const [typedSignature, setTypedSignature] = useState("");
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Device info for submission
  const [deviceInfo, setDeviceInfo] = useState({});

  // Free text values collected from client inputs
  const formContentRef = useRef(null);
  const freeTextValuesRef = useRef({});

  // Replace signature-date placeholder for display
  const processedFormContent = useMemo(() => {
    const rawHtml = linkData?.link?.formContent;
    if (!rawHtml) return "";
    const today = new Date().toLocaleDateString("he-IL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return wrapTablesForMobile(
      rawHtml.replace(
        /<span[^>]*class="signature-date-placeholder"[^>]*>[^<]*<\/span>/g,
        today
      )
    );
  }, [linkData?.link?.formContent]);

  // Replace free-text placeholders with actual input fields for the client
  const formContentWithInputs = useMemo(() => {
    if (!processedFormContent) return "";
    return processedFormContent.replace(
      /<span[^>]*class="free-text-placeholder"[^>]*data-label="([^"]*)"[^>]*>[^<]*<\/span>/g,
      (_, label) =>
        `<div class="free-text-input-wrapper">
          <label class="free-text-label">${label}</label>
          <input type="text" class="free-text-input" data-label="${label}" placeholder="הקלד/י כאן..." dir="rtl" />
        </div>`
    );
  }, [processedFormContent]);

  // Render: replace signature placeholders with fill buttons / captured signatures
  const renderedHtml = useMemo(
    () => renderSignaturePlaceholders(formContentWithInputs, signatures),
    [formContentWithInputs, signatures]
  );

  // For success/review view: render without action buttons (read-only)
  const reviewHtml = useMemo(
    () =>
      renderSignaturePlaceholders(formContentWithInputs, signatures, {
        showActions: false,
      }),
    [formContentWithInputs, signatures]
  );

  const slotIds = useMemo(
    () => collectSlotIds(linkData?.link?.formContent),
    [linkData?.link?.formContent]
  );

  // Open signature modal for a specific slot
  const openSignatureModal = useCallback((slotId) => {
    setActiveSlotId(slotId);
    setSignatures((prev) => {
      const existing = prev[slotId];
      setSignatureType(existing?.type === "typed" ? "typed" : "drawn");
      setTypedSignature(existing?.type === "typed" ? existing.data : "");
      return prev;
    });
    setHasDrawn(false);
  }, []);

  // Save current free-text values from inputs (preserves them across re-renders)
  const captureFreeTextValues = useCallback(() => {
    if (!formContentRef.current) return;
    const inputs = formContentRef.current.querySelectorAll(".free-text-input");
    const values = { ...freeTextValuesRef.current };
    inputs.forEach((input) => {
      values[input.dataset.label] = input.value || "";
    });
    freeTextValuesRef.current = values;
  }, []);

  // Restore previously captured free-text values into newly rendered inputs
  useEffect(() => {
    if (!formContentRef.current) return;
    const inputs = formContentRef.current.querySelectorAll(".free-text-input");
    inputs.forEach((input) => {
      const saved = freeTextValuesRef.current[input.dataset.label];
      if (saved !== undefined && input.value !== saved) {
        input.value = saved;
      }
    });
  }, [renderedHtml]);

  // Wire up fill / edit / clear buttons inside the dangerously-set HTML
  useEffect(() => {
    if (stage !== STAGES.FORM) return;
    const container = formContentRef.current;
    if (!container) return;

    const handleClick = (e) => {
      const fillBtn = e.target.closest("[data-signature-fill]");
      if (fillBtn) {
        const slotId = fillBtn.dataset.signatureFill;
        captureFreeTextValues();
        openSignatureModal(slotId);
        return;
      }
      const editBtn = e.target.closest("[data-signature-edit]");
      if (editBtn) {
        const slotId = editBtn.dataset.signatureEdit;
        captureFreeTextValues();
        openSignatureModal(slotId);
        return;
      }
      const clearBtn = e.target.closest("[data-signature-clear]");
      if (clearBtn) {
        const slotId = clearBtn.dataset.signatureClear;
        captureFreeTextValues();
        setSignatures((prev) => {
          const next = { ...prev };
          delete next[slotId];
          return next;
        });
      }
    };

    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [stage, renderedHtml, captureFreeTextValues, openSignatureModal]);

  // Load link data on mount
  useEffect(() => {
    const loadLink = async () => {
      try {
        const data = await getSignatureFormPublic(linkId);

        if (!data.success) {
          setErrorMessage(data.errorMessage || "הקישור לא נמצא או שפג תוקפו");
          setStage(STAGES.ERROR);
          return;
        }

        // Form content (and, for signed links, the signatures) are only
        // released by the server after identity verification, so both the
        // pending and already-signed cases go through the VERIFY stage.
        setLinkData(data);
        setStage(STAGES.VERIFY);
      } catch (error) {
        console.error("[CK-6101] Error loading signature link:", {
          errorCode: "CK-6101",
          errorMessage: error.message,
          linkId,
          timestamp: new Date().toISOString(),
        });
        setErrorMessage("אירעה שגיאה בטעינת הטופס");
        setStage(STAGES.ERROR);
      }
    };

    loadLink();

    // Collect device info
    setDeviceInfo({
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
    });
  }, [linkId]);

  // Initialize canvas when opening the signature modal in drawn mode
  useEffect(() => {
    if (activeSlotId && signatureType === "drawn" && canvasRef.current) {
      initCanvas();
    }
  }, [activeSlotId, signatureType]);

  // Initialize canvas
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const container = canvas.parentElement;
    canvas.width = container.clientWidth;
    canvas.height = 200;

    ctx.strokeStyle = "#1f2937";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  // Canvas drawing handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  // Handle verification
  const handleVerify = async () => {
    if (!idNumber.trim() || !phoneNumber.trim()) {
      setVerificationError("יש להזין תעודת זהות ומספר טלפון");
      return;
    }

    setVerificationError("");
    setIsVerifying(true);

    try {
      const result = await verifyPatientIdentity(linkId, idNumber, phoneNumber);

      if (!result.success || !result.verified) {
        setVerificationError(
          result.errorMessage || "הפרטים לא תואמים לפרטים שנמצאים אצלנו במערכת"
        );
        setIsVerifying(false);
        return;
      }

      setDeviceInfo((prev) => ({
        ...prev,
        idNumber,
        phoneNumber,
      }));

      // Merge the verified payload (form content, signatures) into linkData
      setLinkData((prev) => ({
        ...(prev || {}),
        ...result,
        link: { ...(prev?.link || {}), ...(result.link || {}) },
      }));

      if (result.alreadySigned) {
        if (result.signatures && typeof result.signatures === "object") {
          setSignatures(result.signatures);
        }
        setStage(STAGES.SUCCESS);
        return;
      }

      setStage(STAGES.FORM);
    } catch (error) {
      console.error("[CK-6103] Verification error:", {
        errorCode: "CK-6103",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });
      setVerificationError("אירעה שגיאה באימות");
    } finally {
      setIsVerifying(false);
    }
  };

  const closeSignatureModal = () => {
    setActiveSlotId(null);
    setTypedSignature("");
    setHasDrawn(false);
  };

  // Confirm signature for the active slot
  const confirmSignature = () => {
    let data;
    if (signatureType === "drawn") {
      if (!hasDrawn || !canvasRef.current) return;
      data = canvasRef.current.toDataURL("image/png");
    } else {
      const trimmed = typedSignature.trim();
      if (!trimmed) return;
      data = trimmed;
    }

    setSignatures((prev) => ({
      ...prev,
      [activeSlotId]: { data, type: signatureType },
    }));
    closeSignatureModal();
  };

  // Submit the signed form
  const handleSubmit = async () => {
    setSubmitError("");
    captureFreeTextValues();

    const filledSlots = Object.keys(signatures);
    if (slotIds.length > 0 && filledSlots.length === 0) {
      setSubmitError("יש למלא לפחות חתימה אחת לפני שליחת הטופס");
      return;
    }

    setStage(STAGES.SUBMITTING);

    try {
      const result = await submitSignedForm(
        linkId,
        signatures,
        deviceInfo,
        freeTextValuesRef.current
      );

      if (result.success) {
        setStage(STAGES.SUCCESS);
      } else {
        const byCode = {
          LINK_NOT_FOUND: "הקישור לא נמצא. ייתכן שנשלח קישור חדש.",
          LINK_INVALID: "הטופס אינו זמין עוד לחתימה. פנה/י למטפל/ת לקבלת קישור חדש.",
          IDENTITY_NOT_VERIFIED: "נדרש אימות זהות מחדש. רענן/י את הדף ונסה/י שוב.",
        };
        setErrorMessage(
          result.errorMessage || byCode[result.error] || "אירעה שגיאה בשמירת החתימה"
        );
        setStage(STAGES.ERROR);
      }
    } catch (error) {
      console.error("[CK-6105] Submit error:", {
        errorCode: "CK-6105",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });
      setErrorMessage("אירעה שגיאה בשמירת החתימה");
      setStage(STAGES.ERROR);
    }
  };

  // Render loading state
  if (stage === STAGES.LOADING) {
    return (
      <div className="sign-form-page">
        <div className="sign-form-container loading-container">
          <ContentLoader fill message="טוען טופס..." />
        </div>
      </div>
    );
  }

  const clinicHeader = (compact = false) => (
    <PublicClinicHeader
      clinic={linkData?.clinic}
      eyebrow="טופס לחתימה דיגיטלית"
      compact={compact}
    />
  );

  // Render error state
  if (stage === STAGES.ERROR) {
    return (
      <div className="sign-form-page">
        <div className="sign-form-container error-container">
          {clinicHeader(true)}
          <div className="sf-status-circle sf-status-danger">
            <XCircle size={36} weight="fill" />
          </div>
          <h2>לא ניתן להציג את הטופס</h2>
          <p>{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Render success / review state — keep the form visible with completed signatures
  if (stage === STAGES.SUCCESS) {
    return (
      <div className="sign-form-page">
        <div className="sign-form-container form-view-container">
          <div className="form-view-header">
            {clinicHeader(true)}
            <div className="success-banner">
              <CheckCircle size={28} weight="fill" />
              <div>
                <h2>הטופס נחתם בהצלחה!</h2>
                <p>תודה על חתימתך. הטופס החתום נשמר במערכת.</p>
              </div>
            </div>
            <h2 className="form-view-title">{linkData?.link?.formName}</h2>
          </div>

          <div className="form-content form-content--review">
            <div
              className="form-content-html ck-form-doc"
              dangerouslySetInnerHTML={{ __html: reviewHtml }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Render verification stage
  if (stage === STAGES.VERIFY) {
    return (
      <div className="sign-form-page">
        <div className="sign-form-container verify-container">
          {clinicHeader()}

          <div className="verify-header">
            <p className="verify-greeting">
              שלום <strong>{linkData?.link?.patientFirstName}</strong>,
            </p>
            <div className="form-name">
              <FileText size={16} weight="fill" />
              <span>{linkData?.link?.formName}</span>
            </div>
          </div>

          <div className="verify-form">
            <div className="sf-status-circle sf-status-primary">
              <IdentificationCard size={32} weight="fill" />
            </div>
            <h2 className="verify-title">אימות זהות</h2>
            <p className="verify-instruction">
              לצפייה וחתימה על הטופס יש לאמת את זהותך
            </p>

            <div className="form-group">
              <label>
                <IdentificationCard size={18} weight="regular" />
                תעודת זהות
              </label>
              <input
                type="text"
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, ""))}
                placeholder="הזן מספר תעודת זהות"
                maxLength={9}
                inputMode="numeric"
                dir="ltr"
              />
            </div>

            <div className="form-group">
              <label>
                <Phone size={18} weight="regular" />
                מספר טלפון
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                placeholder="הזן מספר טלפון"
                maxLength={10}
                inputMode="tel"
              />
            </div>

            {verificationError && (
              <div className="verification-error">
                <Warning size={18} weight="fill" />
                <span>{verificationError}</span>
              </div>
            )}

            <button
              className="verify-btn"
              onClick={handleVerify}
              disabled={isVerifying || !idNumber.trim() || !phoneNumber.trim()}
            >
              {isVerifying ? (
                <>
                  <LoaderLoop size="xs" />
                  <span>מאמת...</span>
                </>
              ) : (
                <span>אמת ועבור לטופס</span>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render submitting stage
  if (stage === STAGES.SUBMITTING) {
    return (
      <div className="sign-form-page">
        <div className="sign-form-container loading-container">
          <ContentLoader fill message="שומר את החתימה..." />
        </div>
      </div>
    );
  }

  // Render form view stage — inline signing
  return (
    <div className="sign-form-page">
      <div className="sign-form-container form-view-container">
        <div className="form-view-header">
          {clinicHeader(true)}
          <h2 className="form-view-title">{linkData?.link?.formName}</h2>
          {slotIds.length > 0 && (
            <p className="form-view-hint">
              ניתן להוסיף את החתימות בתוך הטופס לפני שליחתו
            </p>
          )}
        </div>

        <div className="form-content" ref={formContentRef}>
          <div
            className="form-content-html"
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        </div>

        {submitError && (
          <div className="verification-error">
            <Warning size={18} weight="fill" />
            <span>{submitError}</span>
          </div>
        )}

        <div className="form-actions">
          <button className="proceed-btn" onClick={handleSubmit}>
            <Check size={20} weight="bold" />
            <span>שלח את הטופס</span>
          </button>
        </div>
      </div>

      {/* Signature capture modal */}
      {activeSlotId && (
        <div className="signature-modal-overlay" onClick={closeSignatureModal}>
          <div
            className="signature-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="signature-modal-header">
              <div className="signature-modal-title">
                <span className="signature-modal-icon">
                  <PencilLine size={18} weight="fill" />
                </span>
                <h3>חתימה {activeSlotId}</h3>
              </div>
              <ModalCloseButton onClick={closeSignatureModal} />
            </div>

            <div className="signature-type-tabs">
              <button
                className={`signature-tab ${signatureType === "drawn" ? "active" : ""}`}
                onClick={() => setSignatureType("drawn")}
              >
                <PencilLine size={20} weight="regular" />
                <span>חתימה ביד</span>
              </button>
              <button
                className={`signature-tab ${signatureType === "typed" ? "active" : ""}`}
                onClick={() => setSignatureType("typed")}
              >
                <Keyboard size={20} weight="regular" />
                <span>הקלדת שם</span>
              </button>
            </div>

            {signatureType === "drawn" ? (
              <div className="signature-canvas-container">
                <canvas
                  ref={canvasRef}
                  className="signature-canvas"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                <button className="clear-btn" onClick={clearCanvas}>
                  <Eraser size={18} weight="regular" />
                  <span>נקה</span>
                </button>
              </div>
            ) : (
              <div className="typed-signature-container">
                <input
                  type="text"
                  className="typed-signature-input"
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder="הקלד/י את שמך המלא"
                />
                {typedSignature && (
                  <div className="typed-signature-preview">
                    "{typedSignature}"
                  </div>
                )}
              </div>
            )}

            <div className="signature-actions">
              <button className="back-btn" onClick={closeSignatureModal}>
                ביטול
              </button>
              <button
                className="submit-btn"
                onClick={confirmSignature}
                disabled={
                  (signatureType === "drawn" && !hasDrawn) ||
                  (signatureType === "typed" && !typedSignature.trim())
                }
              >
                <Check size={20} weight="bold" />
                <span>אשר חתימה</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignFormPage;
